import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowRight, CheckCircle2, MapPin, Star } from "lucide-react";
import { site } from "@/config/site";
import { AREAS } from "@/data/areas";
import { reviews } from "@/data/content";
import { useLeadForm } from "@/components/lead/LeadForm";
import { SEO, breadcrumbLd, faqLd } from "@/components/site/SEO";
import { Reveal, Eyebrow } from "@/components/site/Reveal";
import { FAQ } from "@/components/site/FAQ";
import { CTASection } from "@/components/site/CTASection";

export default function AreaPage() {
  const { slug } = useParams();
  const { openForm } = useLeadForm();
  const area = AREAS.find((a) => a.slug === slug);
  if (!area) return <Navigate to="/areas-we-cover" replace />;
  const others = AREAS.filter((a) => a.slug !== slug);

  return (
    <>
      <SEO
        title={`Blinds ${area.name} | Made to Measure, Measured & Fitted | My Blinds Company`}
        description={`Made-to-measure blinds and shutters in ${area.name}. Free home measuring visit, clear no-obligation quote and professional fitting by My Blinds Company.`}
        path={`/areas/${area.slug}`}
        jsonLd={[
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Areas We Cover", path: "/areas-we-cover" },
            { name: area.name, path: `/areas/${area.slug}` },
          ]),
          faqLd(area.faqs),
        ]}
      />

      {/* HERO */}
      <section className="relative bg-navy-950 text-white overflow-hidden grain" data-testid="area-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24">
          <Reveal className="max-w-3xl">
            <p className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 backdrop-blur px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-white mb-6">
              <MapPin className="h-3.5 w-3.5" /> {area.name}
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl tracking-tight leading-[1.08]">
              Made-to-Measure Blinds &amp; Shutters in {area.name}
            </h1>
            <p className="mt-6 text-base sm:text-lg leading-relaxed text-white/75 max-w-2xl">
              {area.intro}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                data-testid="area-book-btn"
                onClick={() => openForm({ source: `area_${area.slug}` })}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-4 text-sm font-semibold text-navy-900 shadow-2xl transition-all duration-300 hover:-translate-y-0.5"
              >
                Book My Free Home Measure <ArrowRight className="h-4 w-4" />
              </button>
              <a
                href={site.phone.href}
                data-testid="area-call-btn"
                className="inline-flex items-center justify-center rounded-full border border-white/40 bg-white/5 backdrop-blur px-8 py-4 text-sm font-semibold text-white transition-all duration-300 hover:bg-white hover:text-navy-900"
              >
                Call {site.phone.display}
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* LOCAL NOTE + SERVICES */}
      <section className="py-16 sm:py-24" data-testid="area-local">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12">
          <Reveal>
            <Eyebrow>Local Knowledge</Eyebrow>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-slate-900 tracking-tight">
              Blinds for {area.name} homes
            </h2>
            <p className="mt-5 text-base leading-relaxed text-slate-600">{area.local}</p>
            <ul className="mt-7 space-y-3">
              {[
                `Free home design visit anywhere in ${area.name}`,
                "Professional measuring — bays, awkward shapes and all",
                "Hundreds of fabrics and finishes to see at home",
                "Made to measure and fitted by our own specialists",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2.5 text-sm text-slate-700">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" /> {t}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="rounded-2xl border border-warm-200 bg-warm-100/60 p-8">
              <h3 className="font-serif text-2xl text-navy-900">Everything we make &amp; fit in {area.name}</h3>
              <div className="mt-5 flex flex-wrap gap-2.5">
                {[
                  ["Roller Blinds", "/roller-blinds"], ["Roman Blinds", "/roman-blinds"],
                  ["Venetian Blinds", "/venetian-blinds"], ["Vertical Blinds", "/vertical-blinds"],
                  ["Day & Night", "/day-night-blinds"], ["Perfect Fit", "/perfect-fit-blinds"],
                  ["Motorised", "/motorised-blinds"], ["Skylight", "/skylight-blinds"],
                  ["Blackout & Thermal", "/blackout-blinds"], ["Shutters", "/shutters"],
                ].map(([label, to]) => (
                  <Link key={label} to={to} className="rounded-full border border-warm-300 bg-white px-4 py-2 text-xs font-semibold text-navy-900 hover:border-navy-800/50 transition-colors">
                    {label}
                  </Link>
                ))}
              </div>
              <p className="mt-6 text-sm leading-relaxed text-slate-600">
                Not sure what suits your windows? Book a free visit and compare
                real samples in your own rooms — advice is part of the service.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* REVIEW SNAPSHOT */}
      <section className="py-16 sm:py-24 bg-warm-100/60" data-testid="area-reviews">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="mb-10 text-center">
            <Eyebrow className="justify-center">What Our Customers Say</Eyebrow>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-slate-900 tracking-tight">
              Rated {site.google.rating} out of 5 on Google
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {reviews.slice(0, 3).map((r, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <figure className="h-full rounded-2xl border border-warm-200 bg-white p-7 shadow-sm">
                  <div className="flex gap-1 mb-4" aria-label={`${r.rating} out of 5 stars`}>
                    {[...Array(r.rating || 5)].map((_, s) => (
                      <Star key={s} className="h-4 w-4 fill-[#C9A227] text-[#C9A227]" />
                    ))}
                  </div>
                  <blockquote className="text-sm leading-relaxed text-slate-700">“{r.text}”</blockquote>
                  <figcaption className="mt-4 text-xs font-semibold uppercase tracking-wider text-slate-500">
                    {r.name} <span className="font-normal normal-case text-slate-400">· {r.source}</span>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 sm:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-10">
            <Eyebrow className="justify-center">Questions</Eyebrow>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-slate-900 tracking-tight">
              Blinds in {area.name} — FAQs
            </h2>
          </Reveal>
          <FAQ items={area.faqs} idPrefix={`area-${area.slug}-faq`} />
        </div>
      </section>

      {/* OTHER AREAS */}
      <section className="pb-16 sm:pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <h2 className="font-serif text-2xl text-slate-900 tracking-tight mb-5">Other areas we cover</h2>
            <div className="flex flex-wrap gap-3">
              {others.map((a) => (
                <Link
                  key={a.slug}
                  to={`/areas/${a.slug}`}
                  data-testid={`area-link-${a.slug}`}
                  className="inline-flex items-center gap-2 rounded-full border border-warm-300 bg-white px-5 py-2.5 text-sm font-semibold text-navy-900 hover:border-navy-800/40 transition-colors"
                >
                  <MapPin className="h-3.5 w-3.5" /> {a.name}
                </Link>
              ))}
              <Link
                to="/blinds-birmingham"
                className="inline-flex items-center gap-2 rounded-full border border-navy-900 bg-navy-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-navy-800 transition-colors"
              >
                <MapPin className="h-3.5 w-3.5" /> Birmingham
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <CTASection source={`area_${area.slug}_final`} />
    </>
  );
}
